import { useQueries, useQuery } from "@tanstack/react-query";
import { apiClient } from "./apiClient";
import { useSelector } from "react-redux";
import userSelector from "@/store/selectors/userSelector";
import { useGetActiveMunicipality, useGetMunicipalities } from "./getMunicipality";

const getBuildingsExtendedQueryKey = (municipalityId?: string | null, userId?: string) => (
  ['buildings/extended', municipalityId ?? null, userId] as const
);

const getBuildingsExtended = async (municipalityId: string) => {
  const buildings = await apiClient.get('/buildings/:municipalityId/buildings/extended', {
    params: { municipalityId }
  });
  return buildings?.data.payload ?? [];
};

export const useGetBuildingsExtended = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const user = useSelector(userSelector);
  const municipalityId = useGetActiveMunicipality({ enabled });

  return useQuery({
    queryKey: getBuildingsExtendedQueryKey(municipalityId, user?.id),
    queryFn: async () => {
      if (!municipalityId) return [];
      return getBuildingsExtended(municipalityId);
    },
    enabled: enabled && !!municipalityId && !!user?.verifiedEmail && !!user?.verifiedByAdmin,
  });
}

export const useGetBuildingsExtendedAcrossMunicipalities = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const user = useSelector(userSelector);
  const municipalitiesQuery = useGetMunicipalities({ enabled });
  const municipalities = municipalitiesQuery.data;
  const canLoad = enabled && !!user?.verifiedEmail && !!user?.verifiedByAdmin;
  const buildingQueries = useQueries({
    queries: municipalities.map((municipality) => ({
      queryKey: getBuildingsExtendedQueryKey(municipality.id, user?.id),
      queryFn: () => getBuildingsExtended(municipality.id),
      enabled: canLoad,
    })),
  });

  return {
    data: municipalities.flatMap((municipality, index) => (
      (buildingQueries[index]?.data ?? []).map((building) => ({
        ...building,
        municipalityName: municipality.name,
      }))
    )),
    failedMunicipalityNames: municipalities
      .filter((_, index) => buildingQueries[index]?.isError)
      .map((municipality) => municipality.name),
    isLoading: municipalitiesQuery.isLoading || buildingQueries.some((query) => query.isLoading),
  };
};
